/**
 * Corpo de `POST /orders/{id}/requests` em multipart/form-data: os campos do contrato, os itens indexados
 * (`items[n].*`) e as chaves dos anexos repetidas em `attachmentKeys`. Os arquivos não vão aqui: cada um já foi
 * direto ao S3 (orderSupportRequestUpload.ts). Separado de api.ts para poder ser testado sem o cliente HTTP.
 */

/** Item da solicitação: na troca, a quantidade (de 1 até a pedida); na divergência, quanto chegou (0 ou mais). */
export interface SupportRequestItemPayload {
  purchaseOrderItemId: string;
  quantity?: number;
  receivedQuantity?: number;
}

export interface SupportRequestPayload {
  requestType: string;
  /**
   * Id do envio, gerado uma vez por formulário. O pedido aceita várias solicitações do mesmo tipo: é este id que
   * faz o back reconhecer a repetição do mesmo envio (ex.: "Tentar novamente" depois de um 503) e reaproveitar o
   * ticket já criado em vez de abrir outro.
   */
  clientRequestId: string;
  /** Obrigatória, exceto na divergência (itens com `receivedQuantity`), em que vira observação opcional. */
  description: string;
  /** Todos os itens vão num ticket só. */
  items: SupportRequestItemPayload[];
  /** Chaves dos anexos já enviados ao S3, na ordem em que aparecem na tela. */
  attachmentKeys?: string[];
}

export const SUPPORT_REQUEST_ATTACHMENT_KEYS_FIELD = "attachmentKeys";

/**
 * SHA-256 do arquivo em hexadecimal minúsculo. Vai no pedido da URL de envio de cada anexo: o back confere, ao ler o
 * arquivo do S3, que ele chegou intacto antes de mandá-lo ao Zendesk.
 */
export async function sha256Hex(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function toSupportRequestFormData(request: SupportRequestPayload): FormData {
  const formData = new FormData();
  formData.append("requestType", request.requestType);
  formData.append("clientRequestId", request.clientRequestId);
  formData.append("description", request.description);
  request.items.forEach((item, index) => {
    formData.append(`items[${index}].purchaseOrderItemId`, item.purchaseOrderItemId);
    if (item.quantity !== undefined) formData.append(`items[${index}].quantity`, String(item.quantity));
    if (item.receivedQuantity !== undefined) {
      formData.append(`items[${index}].receivedQuantity`, String(item.receivedQuantity));
    }
  });
  for (const key of request.attachmentKeys ?? []) {
    formData.append(SUPPORT_REQUEST_ATTACHMENT_KEYS_FIELD, key);
  }
  return formData;
}
